import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { liveApi, type AvailabilitySlot, type BookedWindow, type LiveTutor } from "../../lib/live-api";

interface FreeSlot {
  starts_at: Date;
  ends_at: Date;
}

function subtract(slots: AvailabilitySlot[], booked: BookedWindow[]): FreeSlot[] {
  // Split each slot by booked windows.
  const out: FreeSlot[] = [];
  for (const s of slots) {
    let segments: Array<[Date, Date]> = [[new Date(s.starts_at), new Date(s.ends_at)]];
    for (const b of booked) {
      const bs = new Date(b.start);
      const be = new Date(b.end);
      const next: Array<[Date, Date]> = [];
      for (const [a, c] of segments) {
        if (be <= a || bs >= c) { next.push([a, c]); continue; }
        if (bs > a) next.push([a, bs]);
        if (be < c) next.push([be, c]);
      }
      segments = next;
    }
    for (const [a, c] of segments) {
      if (c.getTime() - a.getTime() >= 30 * 60_000) out.push({ starts_at: a, ends_at: c });
    }
  }
  return out;
}

function fmtTime(d: Date): string {
  return d.toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" });
}

export function LiveBook(): JSX.Element {
  const [tutors, setTutors] = useState<LiveTutor[] | null>(null);
  const [selected, setSelected] = useState<LiveTutor | null>(null);
  const [slots, setSlots] = useState<FreeSlot[] | null>(null);
  const [picked, setPicked] = useState<{ start: Date; durationMin: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    void (async () => {
      try {
        const { tutors } = await liveApi.listTutors();
        setTutors(tutors);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  async function chooseTutor(t: LiveTutor): Promise<void> {
    setSelected(t);
    setSlots(null);
    setPicked(null);
    try {
      const { slots, booked } = await liveApi.getAvailability(t.id);
      setSlots(subtract(slots, booked));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function book(): Promise<void> {
    if (!selected || !picked) return;
    setBusy(true);
    setErr(null);
    try {
      const lesson = await liveApi.bookLesson(selected.id, picked.start, picked.durationMin);
      nav(`/live/lesson/${lesson.id}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-8 max-w-5xl mx-auto text-ink-200">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-light tracking-tight">Znajdź tutora</h1>
        <Link to="/live" className="text-sm text-ink-400 hover:text-ink-200 underline">
          ← Sesje live
        </Link>
      </div>

      {err && <div className="mb-4 px-3 py-2 rounded bg-red-900/40 border border-red-700 text-red-200 text-sm">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h2 className="text-sm uppercase tracking-widest text-ink-400 mb-3">Tutorzy</h2>
          {tutors === null ? (
            <div className="text-ink-500 text-sm">Ładowanie…</div>
          ) : tutors.length === 0 ? (
            <div className="text-ink-500 text-sm">Brak tutorów. Aby zostać tutorem, otwórz "Sesje live" i wybierz rolę Tutor.</div>
          ) : (
            <ul className="space-y-2">
              {tutors.map((t) => (
                <li key={t.id}>
                  <button
                    onClick={() => void chooseTutor(t)}
                    className={`w-full text-left rounded-lg border px-3 py-2 ${selected?.id === t.id ? "border-lime-500 bg-ink-800/60" : "border-ink-700 hover:border-ink-500 bg-ink-800/30"}`}
                  >
                    <div className="font-medium">{t.name ?? t.email ?? "—"}</div>
                    {t.email && <div className="text-xs text-ink-400">{t.email}</div>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h2 className="text-sm uppercase tracking-widest text-ink-400 mb-3">Wolne terminy</h2>
          {!selected ? (
            <div className="text-ink-500 text-sm">Wybierz tutora z listy.</div>
          ) : slots === null ? (
            <div className="text-ink-500 text-sm">Ładowanie terminów…</div>
          ) : slots.length === 0 ? (
            <div className="text-ink-500 text-sm">Brak wolnych terminów w najbliższych 14 dniach.</div>
          ) : (
            <>
              <ul className="space-y-2 mb-4">
                {slots.map((s, i) => {
                  const isPicked = picked && picked.start.getTime() === s.starts_at.getTime();
                  return (
                    <li key={i}>
                      <button
                        onClick={() => setPicked({ start: s.starts_at, durationMin: 60 })}
                        className={`w-full text-left rounded-lg border px-3 py-2 ${isPicked ? "border-lime-500 bg-ink-800/60" : "border-ink-700 hover:border-ink-500 bg-ink-800/30"}`}
                      >
                        <div className="text-sm">{fmtTime(s.starts_at)} – {fmtTime(s.ends_at)}</div>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {picked && (
                <div className="border-t border-ink-700 pt-4">
                  <label className="block text-xs text-ink-400 mb-1">Długość lekcji (min)</label>
                  <select
                    className="bg-ink-800 border border-ink-700 rounded px-2 py-1 mb-3"
                    value={picked.durationMin}
                    onChange={(e) => setPicked({ ...picked, durationMin: Number(e.target.value) })}
                  >
                    <option value={30}>30</option>
                    <option value={45}>45</option>
                    <option value={60}>60</option>
                    <option value={90}>90</option>
                  </select>
                  <button
                    disabled={busy}
                    onClick={() => void book()}
                    className="block px-4 py-2 rounded bg-lime-500/90 hover:bg-lime-400 text-ink-900 font-medium disabled:opacity-50"
                  >
                    {busy ? "Rezerwuję…" : `Zarezerwuj na ${fmtTime(picked.start)}`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
